import { Component, Prop, State, h, Build, Host, Element } from '@stencil/core';
import { ACTION_REGISTRY } from '../../../../../actions/_generated-registry';
import { runElementActions } from '../../../../../actions/runtime';

const COMPONENT_NAME = 'sn-hero-header-type-2';
const CTA_ACTION = 'ui:sn-open-editor' as const;
const ACTIONS_ATTR = 'trigger-actions';
const DATA_ATTR = 'action-data';

/**
 * Hero header component for SignNow forms pages with background document preview image,
 * a "Get Form" CTA button, and an interactive "Show details / Hide details" toggle that
 * reveals a short description while hiding the IRS description block.
 * The background image adapts to viewport size (default, large).
 * Clicking "Get Form" or the heading triggers a form-editor loading flow via
 * the `sn-popup-form-loader` component. All text content is inline-editable via slots.
 *
 * @product signnow
 * @source intro
 * @version 2.0.0
 * @category SignNow
 *
 * @slot heading - {required}, <me-heading variant="1" tag="div"> - Main hero heading text (the component already wraps this slot in the page <h1>, so the slotted me-heading must render a non-heading `div` to avoid a nested/duplicate h1)
 * @slot description - <me-text tag="p"> - Section description text below the heading
 * @slot short-description - <me-text tag="p"> - Expandable short description shown in the details panel
 * @slot irs-description - <me-text tag="p"> - IRS disclaimer text shown by default
 *
 * @example
 *   <sn-hero-header-type-2
 *     form-id="2"
 *     button-text="Get Form"
 *     show-details-text="Show details"
 *     hide-details-text="Hide details"
 *     image-default="https://www.signnow.com/preview/454/879/454879723/large.png"
 *     image-large="https://www.signnow.com/preview/454/879/454879723/large.png"
 *   >
 *     <me-heading slot="heading" variant="1" tag="div">Lease Agreement Form</me-heading>
 *     <me-text slot="description" tag="p">Create a custom Lease Agreement Form that meets your industry specifications.</me-text>
 *     <me-text slot="short-description" tag="p">Residential Lease Agreement THIS LEASE AGREEMENT hereinafter referred to as the Agreement is entered into between the landlord and the tenant.</me-text>
 *     <me-text slot="irs-description" tag="p">We are not affiliated with any brand or entity on this form.</me-text>
 *   </sn-hero-header-type-2>
 */
@Component({
  tag: 'sn-hero-header-type-2',
  styleUrl: 'index.scss',
  shadow: false,
})
export class SnHeroHeaderType2 {
  @Prop() colorMode: 'signnow' | 'inverted' = 'signnow';

  /**
   * Unique form identifier used for the editor loading flow
   */
  @Prop({ attribute: 'form-id', reflect: true }) formId!: string;

  /**
   * CTA button label text
   * @content
   * @default Get Form
   */
  @Prop({ attribute: 'button-text' }) buttonText: string = 'Get Form';

  /**
   * Label for the "Show details" toggle button
   * @content
   * @default Show details
   */
  @Prop({ attribute: 'show-details-text' }) showDetailsText: string = 'Show details';

  /**
   * Label for the "Hide details" toggle button
   * @content
   * @default Hide details
   */
  @Prop({ attribute: 'hide-details-text' }) hideDetailsText: string = 'Hide details';

  /**
   * Background image URL for default / mobile viewports
   */
  @Prop({ attribute: 'image-default' }) imageDefault: string = '';

  /**
   * Background image URL for large viewports (960px+)
   */
  @Prop({ attribute: 'image-large' }) imageLarge: string = '';

  @Element() host!: HTMLElement;

  @State() detailsOpen = false;

  private toggleDetails = () => { this.detailsOpen = !this.detailsOpen; };

  /**
   * The heading is a second trigger for the same intent, and it declares that in the markup rather
   * than forwarding a click to the button.
   *
   * Declaring is what the markup should say: the heading really is a trigger for this action, and
   * anything reading the document — the template builder, a future runner, a person — can see that
   * without tracing a forwarding handler.
   *
   * Running the action still happens once, in one place: the button's rendered element is handed
   * over as the acting element, so both triggers share the single-flight marker and the loading
   * state lands on the button whichever one was clicked.
   */
  private handleH1Click = (event: Event) => {
    event.preventDefault();
    if (!Build.isBrowser) return;
    const cta = this.host.querySelector<HTMLElement>(`.${COMPONENT_NAME}__cta`);
    const acting = cta?.querySelector<HTMLElement>('button') ?? cta;
    void runElementActions(event.currentTarget as HTMLElement, event, this, acting);
  };

  private handleH1KeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      this.handleH1Click(event);
    }
  };

  componentDidLoad() {
    if (!Build.isBrowser) return;

    // Warm the action module while the main thread is idle. `ACTION_REGISTRY` resolves actions
    // with a dynamic import(), so without this the ~600B chunk is fetched at click time and the
    // visitor waits for it: measured 535ms on CPU 4x + Slow 4G versus 101ms warmed. The import is
    // memoised, so this is a prefetch, not a second load. Idle pattern matches one-tap/type-1.
    const warm = () => { void ACTION_REGISTRY[CTA_ACTION]?.(); };
    if ('requestIdleCallback' in window) {
      (window as unknown as { requestIdleCallback: (cb: () => void, o?: object) => void })
        .requestIdleCallback(warm, { timeout: 500 });
    } else {
      setTimeout(warm, 100);
    }

    // Readiness contract for automation. `load` says nothing about whether the CTA works — the
    // Stencil loader is a module script and cannot run before the document is fully parsed, so on
    // this page interactivity lands after `load`, where it landed well before it on the
    // pre-migration page. Tests must wait for this attribute. See MEP-1367.
    this.host.setAttribute('data-cta-ready', '');
  }

  render() {
    return (
      <Host data-color-mode={this.colorMode}>
        <section class={COMPONENT_NAME} data-formid={this.formId}>
          {this.imageDefault && (
            <picture class={`${COMPONENT_NAME}__background`}>
              {this.imageLarge && <source media="(min-width: 960px)" srcSet={this.imageLarge} />}
              {/*
                This is the hero artwork, so on a forms page it is the LCP element. `eager` alone
                only keeps it out of the lazy queue — it still starts at the browser's default
                "low" priority behind the scripts. `fetchpriority="high"` moves it to the front of
                the queue, which is where the head preload was supposed to put it: that preload is
                emitted from stored page analysis data and on production it both points at a
                different form's preview and carries `media="(min-width: 960px)"`, so on mobile the
                LCP image gets no priority at all. Marking it here works regardless of the head.
              */}
              <img
                src={this.imageDefault}
                alt=""
                loading="eager"
                decoding="async"
                class={`${COMPONENT_NAME}__background-img`}
                {...({ fetchpriority: 'high' } as any)}
              />
            </picture>
          )}

          <div class={`${COMPONENT_NAME}__content`}>
            {/* eslint-disable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex */}
            <h1
              class={`${COMPONENT_NAME}__heading`}
              onClick={this.handleH1Click}
              onKeyDown={this.handleH1KeyDown}
              tabIndex={0}
              {...{ [ACTIONS_ATTR]: CTA_ACTION, [DATA_ATTR]: JSON.stringify({ [CTA_ACTION]: { formId: this.formId } }) }}
            >
              <slot name="heading"></slot>
            </h1>
            {/* eslint-enable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex */}

            <div class={`${COMPONENT_NAME}__description`}>
              <slot name="description"></slot>
            </div>

            {/*
              The editor flow is bound as an action rather than a clickHandler so it lives in
              `source/actions`, reachable from code that already ships in this component's and
              me-button's preloaded chunks. me-button runs actions BEFORE clickHandler, so the two
              must not both be set or the flow would fire twice.

              `triggerActions` is bound as a STRING, not an array, and that matters: Stencil's
              reflection copies the property value into the host vnode's attrs, and setAccessor
              routes complex values (arrays, objects) down the property branch — so an array never
              reaches the markup at all. A string reflects to `trigger-actions="…"`, which is what
              makes the binding visible in the served HTML. Several actions are expressed as a
              comma-separated list; see parseActionNames.
            */}
            <me-button
              variant="primary"
              tag="button"
              class={`${COMPONENT_NAME}__cta js-editor-link`}
              triggerActions={CTA_ACTION}
              actionData={{ [CTA_ACTION]: { formId: this.formId } }}
            >
              {this.buttonText}
            </me-button>
          </div>

          <div
            class={{
              [`${COMPONENT_NAME}__details`]: true,
              [`${COMPONENT_NAME}__details--open`]: this.detailsOpen,
            }}
          >
            <button
              class={`${COMPONENT_NAME}__details-toggle`}
              onClick={this.toggleDetails}
              aria-expanded={this.detailsOpen ? 'true' : 'false'}
              aria-controls={`${COMPONENT_NAME}-details-panel`}
            >
              <span>{this.detailsOpen ? this.hideDetailsText : this.showDetailsText}</span>
              <me-icon
                name="arrows--chevron-down"
                class={`${COMPONENT_NAME}__details-icon${this.detailsOpen ? ` ${COMPONENT_NAME}__details-icon--open` : ''}`}
              ></me-icon>
            </button>

            <div
              id={`${COMPONENT_NAME}-details-panel`}
              class={{
                [`${COMPONENT_NAME}__details-panel`]: true,
                [`${COMPONENT_NAME}__details-panel--open`]: this.detailsOpen,
              }}
              role="region"
            >
              <div class={{
                [`${COMPONENT_NAME}__short-description`]: true,
                [`${COMPONENT_NAME}__short-description--hidden`]: !this.detailsOpen,
              }}>
                <slot name="short-description"></slot>
              </div>
            </div>

            <div class={{
              [`${COMPONENT_NAME}__irs-description`]: true,
              [`${COMPONENT_NAME}__irs-description--hidden`]: this.detailsOpen,
            }}>
              <slot name="irs-description"></slot>
            </div>
          </div>
        </section>
      </Host>
    );
  }
}
